import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { DRAW_TOOL_LABEL, type DrawTool, type ShapeKind } from '../doc/schema';
import {
  IconArrow,
  IconBrush,
  IconBucket,
  IconEllipse,
  IconEraser,
  IconFill,
  IconFineliner,
  IconHalftone,
  IconLasso,
  IconLayers,
  IconLine,
  IconMarker,
  IconMove,
  IconPen,
  IconPencil,
  IconPicker,
  IconPixel,
  IconRect,
  IconSpray,
} from '../ui/icons';
import { FEATHERS, type CutoutMode } from './cutout';
import { ColorPicker, OpacitySlider, SizePicker } from '../ui/ColorPicker';
import { useBoard, usePane, usePaneStore } from './context';
import { HandleControls, useFloatingCard } from './floating';
import { BRUSH_CONTROLS, BRUSH_LABEL, BRUSH_SIZE_RANGE, isStrokeBrush, type BrushKind } from './paint/brushes';
import { rasterStore } from './paint/rasterStore';
import { PRESET_GROUP_NAME, SHEET_DPIS, SHEET_PRESETS, isPaperPreset, presetById, sheetBoardSize, sheetSizeIssue, sheetSpec } from './paint/presets';
import { usePanel } from './panels';
import { isInkMode, setBrushSettings, setInkStyle, setPenMode, setToolSettings, useToolSettings, type PenMode } from './toolSettings';

const SHAPES: { kind: ShapeKind; label: string; icon: () => React.JSX.Element }[] = [
  { kind: 'rect', label: 'Rectangle', icon: IconRect },
  { kind: 'ellipse', label: 'Ellipse', icon: IconEllipse },
  { kind: 'line', label: 'Line', icon: IconLine },
  { kind: 'arrow', label: 'Arrow', icon: IconArrow },
];

type ModeButton = { mode: PenMode; label: string; icon: () => React.JSX.Element };

const INK_TOOLS: ModeButton[] = [
  { mode: 'pen', label: 'Pen: pressure sets the width (P)', icon: IconPen },
  { mode: 'fineliner', label: 'Fineliner: always the same width (U)', icon: IconFineliner },
  { mode: 'brush', label: 'Brush pen: strong pressure, pointed ends (B)', icon: IconBrush },
  { mode: 'highlighter', label: 'Highlighter (M)', icon: IconMarker },
  { mode: 'fill', label: 'Fill: paints the inside of the loop you draw (G)', icon: IconFill },
];

const INK_HELPERS: ModeButton[] = [
  { mode: 'eraser', label: 'Eraser (E)', icon: IconEraser },
  { mode: 'lasso', label: 'Lasso: select strokes (L)', icon: IconLasso },
  { mode: 'picker', label: 'Colour picker: take a colour from the board (or hold Alt while drawing)', icon: IconPicker },
];

/** Tip sizes offered per tool, in screen pixels. Fills have none. */
const INK_SIZES: Record<DrawTool, number[]> = {
  pen: [2, 4, 8, 14, 24],
  fineliner: [1, 2, 3, 5, 8, 12],
  brush: [4, 8, 14, 24, 40],
  highlighter: [12, 24, 40],
  fill: [],
};

const CUT_MODES: { mode: CutoutMode; label: string; icon: () => React.JSX.Element }[] = [
  { mode: 'lasso', label: 'Lasso (freehand outline)', icon: IconLasso },
  { mode: 'rect', label: 'Rectangle', icon: IconRect },
  { mode: 'ellipse', label: 'Ellipse', icon: IconEllipse },
];

type BrushButton = { kind: BrushKind; icon: () => React.JSX.Element; key: string; hint: string };

/** What paints, then what edits and selects. */
const PAINT_BRUSHES: BrushButton[] = [
  { kind: 'pencil', icon: IconPencil, key: 'P', hint: 'graphite on the paper’s tooth, darker as you press' },
  { kind: 'fineliner', icon: IconFineliner, key: 'F', hint: 'always the same width' },
  { kind: 'brush', icon: IconBrush, key: 'B', hint: 'soft or hard round' },
  { kind: 'pixel', icon: IconPixel, key: 'X', hint: 'whole pixels, no anti-aliasing' },
  { kind: 'halftone', icon: IconHalftone, key: 'T', hint: 'shades with the dots of a screen' },
  { kind: 'spray', icon: IconSpray, key: 'A', hint: 'a cloud of specks that builds up' },
];

const PAINT_HELPERS: BrushButton[] = [
  { kind: 'eraser', icon: IconEraser, key: 'E', hint: '' },
  { kind: 'fill', icon: IconBucket, key: 'G', hint: '' },
  { kind: 'picker', icon: IconPicker, key: 'I', hint: '' },
  { kind: 'lasso', icon: IconLasso, key: 'L', hint: 'select pixels with a loop' },
  { kind: 'move', icon: IconMove, key: 'V', hint: 'move the selected pixels, or the whole layer' },
];

/** Options card next to the toolbar for tools that have settings (shape, draw, sheet). */
export function ToolOptions() {
  const tool = usePane((s) => s.tool);
  const painting = usePane((s) => s.paintSheetId !== null);
  if (tool === 'sheet')
    return (
      <OptionsPanel title={painting ? 'Paint' : 'Sketch sheet'}>
        {painting ? <PaintOptions /> : <SheetOptions />}
      </OptionsPanel>
    );
  if (tool === 'shape')
    return (
      <OptionsPanel title="Shape">
        <ShapeOptions />
      </OptionsPanel>
    );
  if (tool === 'draw')
    return (
      <OptionsPanel title="Draw">
        <DrawOptions />
      </OptionsPanel>
    );
  if (tool === 'cutout')
    return (
      <OptionsPanel title="Cut out">
        <CutoutOptions />
      </OptionsPanel>
    );
  return null;
}

/**
 * The card itself. Docked beside the toolbar by default; dragging its title bar detaches it so
 * it floats anywhere in the pane (the position is remembered per device, shared by all tools).
 * The dock button, or a double-click on the bar, puts it back.
 */
function OptionsPanel({ title, children }: { title: string; children: ReactNode }) {
  const card = useFloatingCard<HTMLDivElement>('tool-options');
  return (
    <div ref={card.ref} className={`tool-options${card.floating ? ' is-floating' : ''}`} style={card.style} onPointerDown={(e) => e.stopPropagation()}>
      <div className="opt-handle" title="Drag to move these options anywhere (double-click docks them back)" {...card.handleProps}>
        <span className="opt-title">{title}</span>
        <HandleControls card={card} />
      </div>
      <div className="opt-body">{children}</div>
    </div>
  );
}

function ShapeOptions() {
  const s = useToolSettings((t) => t);
  return (
    <>
      <div className="opt-row">
        {SHAPES.map(({ kind, label, icon: Icon }) => (
          <button
            key={kind}
            type="button"
            className={`tb-btn${s.shapeKind === kind ? ' is-active' : ''}`}
            title={label}
            aria-label={label}
            onClick={() => setToolSettings({ shapeKind: kind })}
          >
            <Icon />
          </button>
        ))}
      </div>
      <div className="opt-label">Fill</div>
      <ColorPicker value={s.fill} onChange={(fill) => setToolSettings({ fill })} allowNone label="Fill" />
      <div className="opt-label">Stroke</div>
      <ColorPicker value={s.stroke} onChange={(stroke) => setToolSettings({ stroke })} allowNone label="Stroke" />
      <SizePicker value={s.strokeWidth} options={[1, 2, 3, 6, 10]} onChange={(strokeWidth) => setToolSettings({ strokeWidth })} />
    </>
  );
}

function ModeRow({ modes, current }: { modes: ModeButton[]; current: PenMode }) {
  return (
    <div className="opt-row">
      {modes.map(({ mode, label, icon: Icon }) => (
        <button
          key={mode}
          type="button"
          className={`tb-btn${current === mode ? ' is-active' : ''}`}
          title={label}
          aria-label={label}
          aria-pressed={current === mode}
          onClick={() => setPenMode(mode)}
        >
          <Icon />
        </button>
      ))}
    </div>
  );
}

function DrawOptions() {
  const s = useToolSettings((t) => t);
  const ink = isInkMode(s.penMode) ? s.penMode : null;
  const target = DRAW_TOOL_LABEL[s.inkTool].toLowerCase();
  return (
    <>
      <ModeRow modes={INK_TOOLS} current={s.penMode} />
      <ModeRow modes={INK_HELPERS} current={s.penMode} />
      {ink && (
        <>
          <ColorPicker value={s.ink[ink].color} onChange={(color) => setInkStyle(ink, { color })} label={`${DRAW_TOOL_LABEL[ink]} colour`} />
          {INK_SIZES[ink].length > 0 && (
            <SizePicker value={s.ink[ink].size} options={INK_SIZES[ink]} onChange={(size) => setInkStyle(ink, { size })} />
          )}
          <OpacitySlider value={s.ink[ink].opacity} onChange={(opacity) => setInkStyle(ink, { opacity })} />
        </>
      )}
      {ink === 'fill' && <div className="opt-hint">Draw around an area to paint it. Fills sit on a layer of their own, under the lines.</div>}
      {ink && ink !== 'fill' && (
        <label
          className="opt-check"
          title="Keep the pen still for a moment at the end of a line and it becomes straight; keep moving to aim it. Shift draws straight lines in 15 degree steps"
        >
          <input type="checkbox" checked={s.holdStraighten} onChange={(e) => setToolSettings({ holdStraighten: e.target.checked })} />
          Pause to straighten lines
        </label>
      )}
      {s.penMode === 'eraser' && (
        <>
          <SizePicker value={s.eraserSize} options={[12, 24, 48]} onChange={(eraserSize) => setToolSettings({ eraserSize })} />
          <div className="opt-seg" role="group" aria-label="What the eraser removes">
            <button type="button" className={s.eraserWhole ? '' : 'is-active'} aria-pressed={!s.eraserWhole} onClick={() => setToolSettings({ eraserWhole: false })}>
              Partial
            </button>
            <button type="button" className={s.eraserWhole ? 'is-active' : ''} aria-pressed={s.eraserWhole} onClick={() => setToolSettings({ eraserWhole: true })}>
              Whole strokes
            </button>
          </div>
          <div className="opt-hint">
            {s.eraserWhole ? 'Removes every stroke it touches.' : 'Rubs out only what it covers: lines split in two, fills get a hole.'}
          </div>
        </>
      )}
      {s.penMode === 'lasso' && (
        <div className="opt-hint">Draw a loop around strokes to select them (Shift adds). Tap one to pick it. Drag a selected stroke to move; Delete removes.</div>
      )}
      {s.penMode === 'picker' && (
        <>
          <div className="opt-label">Colour for the {target}</div>
          <ColorPicker
            value={s.ink[s.inkTool].color}
            onChange={(color) => setInkStyle(s.inkTool, { color })}
            label={`${DRAW_TOOL_LABEL[s.inkTool]} colour`}
          />
          <div className="opt-hint">
            Press on the board and move to preview; let go to take the colour and return to the {target}. Holding Alt picks a colour without
            leaving the pen.
          </div>
        </>
      )}
      <label className="opt-check" title="While drawing, a single finger never draws or pans; two fingers still pan and zoom. Other tools are not affected">
        <input type="checkbox" checked={s.penOnly} onChange={(e) => setToolSettings({ penOnly: e.target.checked })} />
        Pen only (fingers don't draw)
      </label>
    </>
  );
}

function CutoutOptions() {
  const s = useToolSettings((t) => t);
  return (
    <>
      <div className="opt-row">
        {CUT_MODES.map(({ mode, label, icon: Icon }) => (
          <button
            key={mode}
            type="button"
            className={`tb-btn${s.cutoutMode === mode ? ' is-active' : ''}`}
            title={label}
            aria-label={label}
            onClick={() => setToolSettings({ cutoutMode: mode })}
          >
            <Icon />
          </button>
        ))}
      </div>
      <div className="opt-label">Soft edge</div>
      <SizePicker value={s.cutoutFeather} options={FEATHERS} onChange={(cutoutFeather) => setToolSettings({ cutoutFeather })} />
      <label className="opt-check" title="Also remove the piece from the image it was cut from (a locked image is left whole)">
        <input type="checkbox" checked={s.cutoutPunch} onChange={(e) => setToolSettings({ cutoutPunch: e.target.checked })} />
        Leave a hole in the source
      </label>
      <div className="opt-hint">
        Draw around part of an image. The piece becomes its own image on top of the original, selected, so it can be dragged away. Where
        images overlap, select the one to cut from first.
      </div>
    </>
  );
}

// ---- sketch sheets ------------------------------------------------------------------------

function SheetOptions() {
  const s = useToolSettings((t) => t);
  const preset = presetById(s.sheetFormat);
  const paper = isPaperPreset(preset);
  const custom = !preset;
  const spec = sheetSpec(s.sheetFormat, { dpi: s.sheetDpi, landscape: s.sheetLandscape, customW: s.sheetCustomW, customH: s.sheetCustomH });
  const issue = sheetSizeIssue(spec.pxW, spec.pxH);
  const size = sheetBoardSize(spec.pxW, spec.pxH, spec.dpi);
  const paperColor = s.sheetPaper;
  return (
    <>
      <div className="opt-label">Format</div>
      <select className="font-select" value={custom ? 'custom' : s.sheetFormat} onChange={(e) => setToolSettings({ sheetFormat: e.target.value })}>
        {(['iso', 'us', 'screen', 'square'] as const).map((g) => (
          <optgroup key={g} label={PRESET_GROUP_NAME[g]}>
            {SHEET_PRESETS.filter((p) => p.group === g).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </optgroup>
        ))}
        <option value="custom">Custom…</option>
      </select>
      {(paper || custom) && (
        <>
          <div className="opt-label">Resolution</div>
          <div className="opt-row opt-wrap">
            {SHEET_DPIS.map((d) => (
              <button key={d} type="button" className={`btn${s.sheetDpi === d ? ' is-active' : ''}`} onClick={() => setToolSettings({ sheetDpi: d })}>
                {d}
              </button>
            ))}
            <span className="opt-unit">dpi</span>
          </div>
        </>
      )}
      {!custom && (
        <div className="opt-row">
          <button type="button" className={`btn${!s.sheetLandscape ? ' is-active' : ''}`} onClick={() => setToolSettings({ sheetLandscape: false })}>
            Portrait
          </button>
          <button type="button" className={`btn${s.sheetLandscape ? ' is-active' : ''}`} onClick={() => setToolSettings({ sheetLandscape: true })}>
            Landscape
          </button>
        </div>
      )}
      {custom && (
        <div className="opt-row">
          <PxField label="W" value={s.sheetCustomW} onCommit={(v) => setToolSettings({ sheetCustomW: v })} />
          <PxField label="H" value={s.sheetCustomH} onCommit={(v) => setToolSettings({ sheetCustomH: v })} />
        </div>
      )}
      <div className="opt-label">Paper</div>
      <ColorPicker value={paperColor} onChange={(sheetPaper) => setToolSettings({ sheetPaper })} allowNone label="Paper colour" />
      <div className={`opt-hint${issue?.level === 'error' ? ' is-bad' : ''}`}>
        {spec.pxW} × {spec.pxH} px · {size.w} × {size.h} on the board
        {issue ? ` · ${issue.text}` : ''}
      </div>
      <div className="opt-hint">Click the board to place the sheet, then paint on it. Double-click a sheet to paint on it later.</div>
    </>
  );
}

/** A pixel size field that commits on blur or Enter. */
function PxField({ label, value, onCommit }: { label: string; value: number; onCommit: (v: number) => void }) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  const commit = () => {
    const n = Math.round(Number(text));
    if (Number.isFinite(n) && n > 0) onCommit(n);
    else setText(String(value));
  };
  return (
    <label className="field">
      <span>{label}</span>
      <input
        type="number"
        min={16}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          e.stopPropagation();
        }}
      />
    </label>
  );
}

function BrushRow({ brushes, current }: { brushes: BrushButton[]; current: BrushKind }) {
  return (
    <div className="opt-row">
      {brushes.map(({ kind: k, icon: Icon, key, hint }) => (
        <button
          key={k}
          type="button"
          className={`tb-btn${current === k ? ' is-active' : ''}`}
          title={`${BRUSH_LABEL[k]}${hint ? `: ${hint}` : ''} (${key})`}
          aria-label={BRUSH_LABEL[k]}
          aria-pressed={current === k}
          onClick={() => setToolSettings({ brush: k })}
        >
          <Icon />
        </button>
      ))}
    </div>
  );
}

function PaintOptions() {
  const s = useToolSettings((t) => t);
  const pane = usePaneStore();
  const board = useBoard();
  const store = rasterStore(board);
  useSyncExternalStore(store.subscribe, store.getVersion);
  const { panel, setPanel } = usePanel();
  const sheetId = usePane((p) => p.paintSheetId);
  const kind = s.brush;
  const b = s.brushes[kind];
  const controls = BRUSH_CONTROLS[kind];
  const [min, max] = BRUSH_SIZE_RANGE[kind];
  const stroke = isStrokeBrush(kind);
  const sheet = sheetId ? board.index.get(sheetId) : undefined;
  const pxW = sheet && sheet.type === 'sheet' ? sheet.props.pxW : 0;
  const selected = !!sheetId && store.selection(sheetId) !== null;
  const selecting = kind === 'lasso' || kind === 'move';
  return (
    <>
      <BrushRow brushes={PAINT_BRUSHES} current={kind} />
      <BrushRow brushes={PAINT_HELPERS} current={kind} />
      {controls.color && <ColorPicker value={s.paintColor} onChange={(paintColor) => setToolSettings({ paintColor })} label="Paint colour" />}
      {stroke && (
        <Slider label="Size" value={b.size} min={min} max={max} step={1} unit="px" log onChange={(size) => setBrushSettings(kind, { size })} />
      )}
      {controls.opacity && (
        <Slider label="Opacity" value={Math.round(b.opacity * 100)} min={1} max={100} step={1} unit="%" onChange={(v) => setBrushSettings(kind, { opacity: v / 100 })} />
      )}
      {controls.hardness && (
        <Slider label="Hardness" value={Math.round(b.hardness * 100)} min={0} max={100} step={1} unit="%" onChange={(v) => setBrushSettings(kind, { hardness: v / 100 })} />
      )}
      {controls.flow && (
        <Slider
          label={kind === 'halftone' ? 'Tone' : 'Flow'}
          value={Math.round(b.flow * 100)}
          min={1}
          max={100}
          step={1}
          unit="%"
          onChange={(v) => setBrushSettings(kind, { flow: v / 100 })}
        />
      )}
      {controls.grain && (
        <Slider label="Grain" value={Math.round(b.grain * 100)} min={0} max={100} step={1} unit="%" onChange={(v) => setBrushSettings(kind, { grain: v / 100 })} />
      )}
      {controls.density && (
        <Slider label="Density" value={Math.round(b.density * 100)} min={1} max={100} step={1} unit="%" onChange={(v) => setBrushSettings(kind, { density: v / 100 })} />
      )}
      {controls.pitch && (
        <Slider label="Dots" value={b.pitch} min={4} max={64} step={1} unit="px" log onChange={(pitch) => setBrushSettings(kind, { pitch })} />
      )}
      {kind === 'fill' && (
        <Slider label="Tolerance" value={s.fillTolerance} min={0} max={255} step={1} unit="" onChange={(fillTolerance) => setToolSettings({ fillTolerance })} />
      )}
      {(controls.pressure || controls.pressureOpacity) && (
        <div className="opt-row opt-wrap">
          {controls.pressure && (
            <label className="opt-check" title="Pen pressure changes the size">
              <input type="checkbox" checked={b.pressureSize} onChange={(e) => setBrushSettings(kind, { pressureSize: e.target.checked })} />
              Pressure → size
            </label>
          )}
          {controls.pressureOpacity && (
            <label
              className="opt-check"
              title={
                kind === 'pencil' ? 'Pen pressure changes how dark the line is' : kind === 'halftone' ? 'Pen pressure changes how large the dots grow' : 'Pen pressure changes the opacity'
              }
            >
              <input type="checkbox" checked={b.pressureOpacity} onChange={(e) => setBrushSettings(kind, { pressureOpacity: e.target.checked })} />
              {controls.pressure ? '→ ' : 'Pressure → '}
              {kind === 'pencil' ? 'darkness' : kind === 'halftone' ? 'tone' : 'opacity'}
            </label>
          )}
        </div>
      )}
      {controls.aliased && (
        <label
          className="opt-check"
          title={
            kind === 'lasso'
              ? 'Every pixel is either selected or not: moving a piece leaves no soft seam. Meant for pixel art'
              : 'Rubs out whole pixels, with no anti-aliased edge. Meant for pixel art'
          }
        >
          <input type="checkbox" checked={b.aliased} onChange={(e) => setBrushSettings(kind, { aliased: e.target.checked })} />
          Hard pixel edge
        </label>
      )}
      {selecting && sheetId && (
        <div className="btn-row">
          <button type="button" className="btn" title="Select the whole sheet (Ctrl+A)" onClick={() => store.selectAll(sheetId)}>
            All
          </button>
          <button type="button" className="btn" title="Drop the selection (Ctrl+D)" disabled={!selected} onClick={() => store.setSelection(sheetId, null)}>
            Deselect
          </button>
        </div>
      )}
      {kind === 'picker' && <div className="opt-hint">Press on the sheet to pick a colour from the active layer (Alt + press does this with any brush).</div>}
      {kind === 'fill' && <div className="opt-hint">Fills the connected area of the active layer under the press{selected ? ', inside the selection' : ''}.</div>}
      {kind === 'pixel' && <div className="opt-hint">Whole pixels, no anti-aliasing. Zoomed in past 200 % the sheet shows its pixels as squares.</div>}
      {kind === 'halftone' && <div className="opt-hint">Shades with a dot screen fixed to the paper: tone (and pressure) sets how large the dots grow, a low hardness fades them out at the rim.</div>}
      {kind === 'spray' && <div className="opt-hint">Keeps spraying while the pointer rests.</div>}
      {kind === 'lasso' && (
        <div className="opt-hint">Draw a loop around pixels of the sheet. Brushes, the eraser and fills then stay inside it; Delete clears it; a tap outside drops it.</div>
      )}
      {kind === 'move' && (
        <div className="opt-hint">
          Drag to move {selected ? 'the selected pixels' : 'everything on the layer'} (Shift keeps to one axis, arrow keys nudge). The pixels land when you pick
          another tool or layer.
        </div>
      )}
      {stroke && pxW > 0 && <div className="opt-hint">Sizes are sheet pixels ({pxW} px across); zoom does not change the mark.</div>}
      <label className="opt-check" title="While painting, a single finger never paints or pans; two fingers still pan and zoom">
        <input type="checkbox" checked={s.penOnly} onChange={(e) => setToolSettings({ penOnly: e.target.checked })} />
        Pen only (fingers don't paint)
      </label>
      <div className="btn-row">
        <button type="button" className={`btn${panel === 'sheet' ? ' is-active' : ''}`} title="Sheet layers" onClick={() => setPanel(panel === 'sheet' ? null : 'sheet')}>
          <IconLayers /> Layers
        </button>
        <button type="button" className="btn" title="Stop painting (Esc)" onClick={() => pane.getState().setTool('select')}>
          Done
        </button>
      </div>
    </>
  );
}

/**
 * A labelled range with its value. `log` maps the slider to a logarithmic scale so small brush
 * sizes get most of the travel.
 */
function Slider({
  label,
  value,
  min,
  max,
  step,
  unit,
  log = false,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  log?: boolean;
  onChange: (v: number) => void;
}) {
  const toSlider = (v: number) => (log ? Math.log(Math.max(min, v)) : v);
  const fromSlider = (t: number) => (log ? Math.round(Math.exp(t)) : t);
  return (
    <label className="opt-slider">
      <span className="opt-slider-label">{label}</span>
      <input
        type="range"
        min={toSlider(min)}
        max={toSlider(max)}
        step={log ? 0.01 : step}
        value={toSlider(value)}
        onChange={(e) => onChange(Math.max(min, Math.min(max, fromSlider(Number(e.target.value)))))}
      />
      <span className="opt-slider-value">
        {value}
        {unit}
      </span>
    </label>
  );
}
